import { Expo, Power3 } from 'gsap';
import * as PIXI from 'pixi.js';
import Text from '../../graphics/Text';
import DraggablePreset from './DraggablePreset';
import {
  getAlignX,
  getCenterX,
  getCenterY,
  getFixBelowY,
  getFixLeftX,
} from '../../utils/CoordinateCalculator';

const leftSingleOpenQuote = '\u2018 ';
const quoteDefaultStyle = {
  fillGradientType: 1,
  fontFamily: 'Lobster',
  trim: true,
};

export default class Quote extends DraggablePreset {
  constructor(app) {
    super(app);
    this.mainText = new Text(app);
    this.byLine = new Text(app);

    this.quoteLeft = new PIXI.Text(leftSingleOpenQuote);
    this.quoteRight = new PIXI.Text(leftSingleOpenQuote);

    this.container.addChild(
      this.quoteLeft,
      this.quoteRight,
      this.mainText,
      this.byLine
    );
  }

  update(model) {
    super.update(model);

    const fadeInDuration = 0.75;
    const flyInDuration = 1.5;
    const flyOutDuration = 0.75;

    this.setupQuotes(this.fontSize * 3);

    const quoteFontSize = this.quoteLeft.style.fontSize;
    const quoteMoveDistance = this.quoteLeft.width / 2;

    // Text content and Styling
    this.mainText.text =
      this.numTextLines > 1 ? this.getTextLines(0, -1) : this.getTextLine(0);
    this.mainText.updateStyle(model);

    this.byLine.text = this.numTextLines > 1 ? this.getTextLine(-1) : '';
    this.byLine.updateStyle(model, {
      scale: 0.5,
    });

    // Size the root element based on our content
    this.setDraggableRoot(
      this.mainText.lineControls.height +
        this.byLine.lineControls.height +
        this.quoteLeft.height,
      Math.max(
        this.mainText.lineControls.width,
        this.byLine.lineControls.width
      ) +
        this.quoteLeft.width * 4
    );

    // Position elements off of the root element
    this.mainText.lineControls.x = getCenterX(
      this.mainText.lineControls,
      this.root
    );
    this.mainText.lineControls.y = getCenterY(
      this.mainText.lineControls,
      this.root
    );
    this.byLine.lineControls.x = getAlignX(this.byLine.lineControls, this.root);
    this.byLine.lineControls.y = getFixBelowY(
      this.byLine.lineControls,
      this.mainText.lineControls
    );

    // position quotes to where the text will be
    this.quoteLeft.x = this.mainText.lineControls.x;
    this.quoteRight.x = this.mainText.lineControls.x;
    this.quoteLeft.y = this.mainText.lineControls.y;
    this.quoteRight.y = this.mainText.lineControls.y;

    // Store a copy of the origin for later movements
    const origin = {
      x: this.mainText.lineControls.x,
      y: this.mainText.lineControls.y,
      height: this.mainText.lineControls.height,
      width: this.mainText.lineControls.width,
    };

    // Now that we are done with the root placement,
    // readjust to starting positions.
    this.mainText.lineControls.x += quoteMoveDistance * 2;
    this.byLine.lineControls.x -= quoteMoveDistance;
    this.quoteRight.style.fontSize = 1;
    this.quoteLeft.style.fontSize = 1;
    this.quoteLeft.angle = -180;
    this.quoteRight.angle = -180;
    this.quoteLeft.alpha = 0.5;
    this.quoteRight.alpha = 0.5;
    this.mainText.alpha = 0;
    this.byLine.alpha = 0;

    const fadeIn = {
      alpha: 1,
      duration: fadeInDuration,
      ease: Power3.easeOut,
    };
    const flyIn = (x) => ({ x, duration: flyInDuration, ease: Expo.easeOut });
    const flyOut = (x) => ({
      x: x + quoteMoveDistance * 2,
      alpha: 0,
      duration: flyOutDuration,
      ease: Expo.easeIn,
    });

    // Animate the text
    this.animate(this.mainText.lineControls)
      .to(flyIn(getCenterX(this.mainText.lineControls, this.root)), 0)
      .to(fadeIn, 0)
      .to(
        flyOut(getFixLeftX(this.mainText.lineControls, this.root)),
        `end-=${flyOutDuration}`
      );

    this.animate(this.byLine.lineControls)
      .to(flyIn(getAlignX(this.byLine.lineControls, origin)), 0.5)
      .to(fadeIn, 0.5)
      .to(
        flyOut(getAlignX(this.byLine.lineControls, origin)),
        `end-=${flyOutDuration}`
      );

    this.animateQuote(this.quoteLeft, -quoteMoveDistance, quoteFontSize);
    this.animateQuote(this.quoteRight, quoteMoveDistance, quoteFontSize);
  }

  animateQuote(quote, distance, fontSize) {
    const duration = 0.75;
    const twistIn = {
      angle: 0,
      alpha: 1,
      duration,
      ease: Power3.easeOut,
    };

    const moveApart = {
      x: quote.x + distance,
      duration,
      ease: Power3.easeOut,
    };

    const moveOut = {
      x: quote.x,
      alpha: 0,
      duration,
      ease: Power3.easeIn,
    };

    this.animate(quote)
      .to(twistIn, 0)
      .to(moveApart, 0.5)
      .to(moveOut, `end-=${duration}`);

    this.animate(quote.style).to({ duration, fontSize }, 0);
  }

  setupQuotes(fontSize) {
    const gradientColor = this.makeGradientColor();

    const quoteStyle = {
      ...quoteDefaultStyle,
      fontSize,
      stroke: this.secondaryColor.toString(),
      fill: [this.secondaryColor.toString(), gradientColor.toString()],
    };

    this.quoteLeft.style = { ...quoteStyle };
    this.quoteRight.style = { ...quoteStyle };
    this.quoteLeft.anchor.set(0.5);
    this.quoteRight.anchor.set(0.5);
  }

  makeGradientColor() {
    return this.secondaryColor.isLight()
      ? this.secondaryColor.clone().darken().desaturate()
      : this.secondaryColor.clone().lighten(25).desaturate();
  }
}
